import { Link as RemixLink } from "@remix-run/react";
import {
  Heading,
  Box,
  Button,
  SimpleGrid,
  Text,
  Icon,
  Link,
} from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { FaFacebook } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { useLocale } from "#app/context/LocaleContext.jsx";
import { ReviewCard, REVIEW_SOURCE_URLS } from "./ReviewCard.jsx";

export function ReviewSection({
  reviews = [],
  // Every landing page shows the same "wall of love": a masonry of up to 6 cards
  // under a shared heading. These defaults ARE the landing-page treatment, so
  // routes pass only `reviews` + `stats`. Override per-prop for other layouts.
  heading = "Trusted by campers everywhere",
  stats,
  maxReviews = 6,
  wall = true,
}) {
  const locale = useLocale();
  const shown = reviews.slice(0, maxReviews);

  return (
    <Box
      p={{ base: 4, md: 0 }}
      maxW={wall ? "container.lg" : "container.md"}
      mx="auto"
      w="100%"
    >
      <Box textAlign="center" mb={6}>
        <Heading
          as="h2"
          size={{ base: "sm", md: "md" }}
          fontWeight="extrabold"
          mb={4}
        >
          {heading}
        </Heading>
        {stats && stats.total > 0 && (
          <Text fontSize={{ base: "md", md: "lg" }} color="muted">
            Real campers, real saved trips &mdash;{" "}
            <Text as="strong" color="emphasized">
              {stats.total.toLocaleString(locale)}
            </Text>{" "}
            reviews
            {stats.avgGoogleRating && (
              <>
                {" "}
                at a{" "}
                <Text as="strong" color="emphasized">
                  {stats.avgGoogleRating}
                </Text>{" "}
                average
              </>
            )}
            , straight from{" "}
            <Link
              href={REVIEW_SOURCE_URLS.google}
              isExternal
              fontWeight="semibold"
              color="emphasized"
            >
              <Icon
                as={FcGoogle}
                boxSize="1.1em"
                verticalAlign="-0.15em"
                mr="1"
              />
              Google
            </Link>{" "}
            and{" "}
            <Link
              href={REVIEW_SOURCE_URLS.facebook}
              isExternal
              fontWeight="semibold"
              color="emphasized"
            >
              <Icon
                as={FaFacebook}
                color="#1877F2"
                boxSize="1.1em"
                verticalAlign="-0.15em"
                mr="1"
              />
              Facebook
            </Link>
            .
          </Text>
        )}
      </Box>

      {wall ? (
        // Masonry wall — cards pack by their natural height (CSS columns).
        <Box
          mt={6}
          sx={{
            columnGap: "1rem",
            columnCount: 1,
            "@media (min-width: 48em)": { columnCount: 2 },
            "@media (min-width: 62em)": { columnCount: 3 },
          }}
        >
          {shown.map((review, i) => (
            <Box
              key={review.id}
              mb={4}
              sx={{ breakInside: "avoid" }}
              // Mobile keeps it tight: only the first 3 show; the rest fill in
              // once there's room for 2+ columns.
              display={{ base: i < 3 ? "block" : "none", md: "block" }}
            >
              <ReviewCard review={review} variant="compact" />
            </Box>
          ))}
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={6}>
          {shown.map((review) => (
            <ReviewCard key={review.id} review={review} variant="compact" />
          ))}
        </SimpleGrid>
      )}

      <Box textAlign="right" mt={3}>
        <Button
          variant="link"
          colorScheme="brand"
          rightIcon={<ArrowForwardIcon />}
          as={RemixLink}
          to="/reviews"
          size="sm"
        >
          {stats?.total
            ? `See all ${stats.total.toLocaleString(locale)} reviews`
            : "See all reviews"}
        </Button>
      </Box>
    </Box>
  );
}
